import React from "react";
import { Box } from "@mui/material";
import ListPersonAvatar from "./ListPersonAvatar";

export const STATUS_COLUMN_KEYS = [
  "status",
  "leave_status",
  "call_status",
  "employment_status_name",
  "internship_status_name",
  "attendance_status_type_name",
  "attendance_status",
  "payment_status",
  "student_status",
  "is_active",
];

export const PRIORITY_COLUMN_KEYS = ["priority", "priority_level"];

export const NAME_AVATAR_KEYS = [
  "name",
  "employee",
  "employee_name",
  "person_full_name",
  "person_name",
  "full_name",
  "application_user_full_name",
  "intern",
  "intern_name",
  "lead_person_name",
];

/** Report / summary tableNames must not show list name avatars. */
export function isReportTableName(tableName) {
  const t = String(tableName || "").toLowerCase();
  if (!t) return false;
  return (
    t.includes("_summaries") ||
    t.includes("_balances") ||
    t.includes("_report") ||
    t.endsWith("report") ||
    t.includes("monthly_report") ||
    t.includes("without_break")
  );
}

export function isStatusColumnKey(key) {
  if (!key) return false;
  if (STATUS_COLUMN_KEYS.includes(key)) return true;
  const lower = String(key).toLowerCase();
  return (
    lower === "status" ||
    lower.endsWith("_status") ||
    lower.endsWith("_status_name") ||
    lower.endsWith("status_type_name")
  );
}

export function isPriorityColumnKey(key) {
  if (!key) return false;
  const lower = String(key).toLowerCase();
  return PRIORITY_COLUMN_KEYS.includes(lower) || lower.endsWith("_priority");
}

export function isNameAvatarColumn(key, tableName, enableNameAvatar = true) {
  if (!key) return false;
  if (enableNameAvatar === false) return false;
  if (isReportTableName(tableName)) return false;

  const lower = String(key).toLowerCase();
  if (NAME_AVATAR_KEYS.includes(lower)) return true;

  return (
    lower === "name" &&
    String(tableName || "")
      .toLowerCase()
      .includes("employee")
  );
}

/** Map free-text status values to EMS pill colors (dot + tint). */
export function getStatusPillTone(raw) {
  const val = String(raw ?? "").trim();
  if (!val || val === "-") return null;

  // Skip glyph-only active flags (lists that already map is_active → ✔/✖)
  if (/^[✔✖✓✗✖️✔️]+$/.test(val)) return null;

  const lower = val.toLowerCase();
  let dotColor = "#9ca3af";
  let bg = "#f3f4f6";
  let textColor = "#4b5563";

  if (
    lower.includes("approved") ||
    lower.includes("attended") ||
    lower.includes("answered") ||
    lower.includes("completed") ||
    lower.includes("active") ||
    lower.includes("present") ||
    lower.includes("paid") ||
    lower.includes("success") ||
    lower === "true" ||
    lower === "1" ||
    lower === "yes"
  ) {
    dotColor = "#22c55e";
    bg = "#dcfce7";
    textColor = "#15803d";
  } else if (
    lower.includes("reject") ||
    lower.includes("cancel") ||
    lower.includes("inactive") ||
    lower.includes("absent") ||
    lower.includes("failed") ||
    lower.includes("missed") ||
    lower === "no answer" ||
    lower.includes("noanswer") ||
    lower === "false" ||
    lower === "0" ||
    lower === "no"
  ) {
    dotColor = "#ef4444";
    bg = "#fee2e2";
    textColor = "#b91c1c";
  } else if (
    lower.includes("pending") ||
    lower.includes("scheduled") ||
    lower.includes("draft") ||
    lower.includes("applied") ||
    lower.includes("submitted") ||
    lower.includes("busy") ||
    lower.includes("hold") ||
    lower.includes("waiting")
  ) {
    dotColor = "#f59e0b";
    bg = "#fef3c7";
    textColor = "#b45309";
  } else if (
    lower.includes("delivered") ||
    lower.includes("responded") ||
    lower.includes("processing") ||
    lower.includes("in progress") ||
    lower.includes("ongoing")
  ) {
    dotColor = "#0891b2";
    bg = "#cffafe";
    textColor = "#0e7490";
  }

  let label = val;
  if (lower === "true" || lower === "1" || lower === "yes") label = "Active";
  if (lower === "false" || lower === "0" || lower === "no") label = "Inactive";

  return { label, dotColor, bg, textColor };
}

/** Map priority values to pill colors. */
export function getPriorityPillTone(raw) {
  const val = String(raw ?? "").trim();
  if (!val || val === "-" || val === "—") return null;

  const lower = val.toLowerCase();
  if (lower === "none") {
    return { label: val, dotColor: "#9ca3af", bg: "#f3f4f6", textColor: "#4b5563" };
  }
  if (lower === "low") {
    return { label: val, dotColor: "#22c55e", bg: "#dcfce7", textColor: "#15803d" };
  }
  if (lower === "medium") {
    return { label: val, dotColor: "#f59e0b", bg: "#fef3c7", textColor: "#b45309" };
  }
  if (lower === "high") {
    return { label: val, dotColor: "#f97316", bg: "#ffedd5", textColor: "#c2410c" };
  }
  if (lower === "urgent") {
    return { label: val, dotColor: "#ef4444", bg: "#fee2e2", textColor: "#b91c1c" };
  }

  return { label: val, dotColor: "#9ca3af", bg: "#f3f4f6", textColor: "#4b5563" };
}

export function StatusPill({ value, toneFn = getStatusPillTone }) {
  const tone = toneFn(value);
  if (!tone) {
    const fallback = String(value ?? "").trim();
    if (!fallback || fallback === "-" || fallback === "—") {
      return <span style={{ color: "var(--ems-faint)" }}>—</span>;
    }
    return <span>{fallback}</span>;
  }

  return (
    <span
      className="ems-status-pill"
      style={{
        background: tone.bg,
        color: tone.textColor,
      }}
    >
      <span
        className="ems-status-dot"
        style={{ background: tone.dotColor }}
        aria-hidden
      />
      {tone.label}
    </span>
  );
}

export function PriorityPill({ value }) {
  return <StatusPill value={value} toneFn={getPriorityPillTone} />;
}

export function wrapPlainListCell(display) {
  if (React.isValidElement(display)) return display;
  const text = display == null ? "" : String(display);
  return (
    <div className="ems-list-oneline" title={text === "—" ? undefined : text}>
      {display}
    </div>
  );
}

export function NameWithAvatar({ row, name, getNestedValue, onClick, clickable }) {
  const isClickable = Boolean(clickable || onClick);
  return (
    <Box
      className="ems-list-person"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minWidth: 0,
        cursor: isClickable ? "pointer" : "default",
      }}
      onClick={
        isClickable
          ? (e) => {
              e.stopPropagation();
              onClick?.(e);
            }
          : undefined
      }
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onClick?.(e);
              }
            }
          : undefined
      }
    >
      <ListPersonAvatar row={row} getNestedValue={getNestedValue} size={34} />
      <Box
        component="span"
        className="ems-list-oneline"
        title={name === "—" ? undefined : name}
        sx={{
          fontSize: "0.875rem",
          wordBreak: "break-word",
          fontWeight: isClickable ? 650 : 500,
          color: isClickable ? "var(--ems-primary)" : "inherit",
        }}
      >
        {name}
      </Box>
    </Box>
  );
}

export function formatCellDisplay(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (React.isValidElement(value)) return value;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}
